/**
 * DSA Commission Offer Service
 *
 * API service for DSA Commission Marketplace operations.
 * Handles DSA offers browsing, posting, management, and invite flows.
 *
 * Base URL: /api/leadflow/dsa-offers
 * Invite URL: /api/leadflow/dsa-invites
 */

import axiosInstance from '../config/axios.config';

// ================================
// CONSTANTS & CONFIGURATIONS
// ================================

/**
 * DSA Offer status configuration
 */
export const DSA_OFFER_STATUS = {
    active: {
        label: 'Active',
        severity: 'success',
        icon: 'pi-check-circle',
        description: 'Offer is visible and accepting invites'
    },
    paused: {
        label: 'Paused',
        severity: 'warning',
        icon: 'pi-pause',
        description: 'Temporarily hidden from browse'
    },
    expired: {
        label: 'Expired',
        severity: 'secondary',
        icon: 'pi-clock',
        description: 'Offer has expired'
    },
    deleted: {
        label: 'Deleted',
        severity: 'danger',
        icon: 'pi-trash',
        description: 'Offer has been removed'
    }
};

/**
 * DSA Invite status configuration
 */
export const DSA_INVITE_STATUS = {
    pending: {
        label: 'Pending',
        severity: 'warning',
        icon: 'pi-clock',
        description: 'Awaiting DSA response'
    },
    viewed: {
        label: 'Viewed',
        severity: 'info',
        icon: 'pi-eye',
        description: 'DSA has viewed the invite'
    },
    interested: {
        label: 'Accepted',
        severity: 'success',
        icon: 'pi-check-circle',
        description: 'DSA accepted and expressed interest'
    },
    declined: {
        label: 'Declined',
        severity: 'danger',
        icon: 'pi-times-circle',
        description: 'DSA declined the invite'
    },
    expired: {
        label: 'Expired',
        severity: 'secondary',
        icon: 'pi-calendar-times',
        description: 'Invite has expired'
    },
    cancelled: {
        label: 'Cancelled',
        severity: 'secondary',
        icon: 'pi-ban',
        description: 'Lead Owner cancelled the invite'
    }
};

/**
 * Loan types for DSA offers
 */
export const DSA_LOAN_TYPES = [
    { value: 'home_loan', label: 'Home Loan', icon: 'pi-home' },
    { value: 'personal_loan', label: 'Personal Loan', icon: 'pi-user' },
    { value: 'business_loan', label: 'Business Loan', icon: 'pi-briefcase' },
    { value: 'vehicle_loan', label: 'Vehicle Loan', icon: 'pi-car' },
    { value: 'education_loan', label: 'Education Loan', icon: 'pi-book' },
    { value: 'gold_loan', label: 'Gold Loan', icon: 'pi-star' },
    { value: 'lap', label: 'Loan Against Property', icon: 'pi-building' },
    { value: 'working_capital', label: 'Working Capital', icon: 'pi-wallet' },
    { value: 'msme_loan', label: 'MSME Loan', icon: 'pi-shop' },
    { value: 'professional_loan', label: 'Professional Loan', icon: 'pi-id-card' },
    { value: 'overdraft', label: 'Overdraft', icon: 'pi-credit-card' },
    { value: 'credit_card', label: 'Credit Card', icon: 'pi-credit-card' },
    { value: 'insurance', label: 'Insurance', icon: 'pi-shield' },
    { value: 'other', label: 'Other', icon: 'pi-ellipsis-h' }
];

/**
 * Segment options for DSA offers
 */
export const DSA_SEGMENTS = [
    { value: 'retail', label: 'Retail' },
    { value: 'sme', label: 'SME' },
    { value: 'corporate', label: 'Corporate' },
    { value: 'agriculture', label: 'Agriculture' },
    { value: 'msme', label: 'MSME' },
    { value: 'professional', label: 'Professional' }
];

/**
 * Sort options for browsing DSA offers
 */
export const DSA_OFFER_SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'commission', label: 'Highest Commission' },
    { value: 'deadline', label: 'Earliest Deadline' },
    { value: 'views', label: 'Most Viewed' }
];

/**
 * Popular Indian banks and NBFCs for autocomplete
 */
export const POPULAR_BANKS = [
    // Public Sector Banks
    'State Bank of India',
    'Punjab National Bank',
    'Bank of Baroda',
    'Canara Bank',
    'Union Bank of India',
    'Bank of India',
    'Indian Bank',
    'Central Bank of India',
    'Indian Overseas Bank',
    'UCO Bank',
    'Bank of Maharashtra',
    'Punjab & Sind Bank',
    // Private Sector Banks
    'HDFC Bank',
    'ICICI Bank',
    'Axis Bank',
    'Kotak Mahindra Bank',
    'IndusInd Bank',
    'Yes Bank',
    'IDFC First Bank',
    'Federal Bank',
    'RBL Bank',
    'South Indian Bank',
    'Karur Vysya Bank',
    'City Union Bank',
    'DCB Bank',
    'Bandhan Bank',
    'IDBI Bank',
    // NBFCs & HFCs
    'Bajaj Finserv',
    'Tata Capital',
    'L&T Finance',
    'Mahindra Finance',
    'Cholamandalam Finance',
    'Muthoot Finance',
    'Manappuram Finance',
    'Shriram Finance',
    'Piramal Finance',
    'Aditya Birla Capital',
    'Hero FinCorp',
    'HDB Financial Services',
    'Fullerton India',
    'Capital First',
    'IIFL Finance',
    // Housing Finance Companies
    'LIC Housing Finance',
    'HDFC Ltd',
    'PNB Housing Finance',
    'Indiabulls Housing Finance',
    'Can Fin Homes',
    'GIC Housing Finance',
    'Repco Home Finance',
    'Aptus Value Housing',
    'Aavas Financiers',
    'Home First Finance',
    // Small Finance Banks
    'AU Small Finance Bank',
    'Equitas Small Finance Bank',
    'Ujjivan Small Finance Bank',
    'Jana Small Finance Bank',
    'Fincare Small Finance Bank',
    // Fintech Lenders
    'Lendingkart',
    'Capital Float',
    'Flexiloans',
    'NeoGrowth',
    'Indifi',
    'Clix Capital',
    'MoneyTap',
    'KreditBee',
    'EarlySalary',
    'PaySense'
];

// ================================
// API SERVICE METHODS - DSA OFFERS
// ================================

export const dsaOfferService = {
    // ================================
    // DSA OFFER MANAGEMENT
    // ================================

    /**
     * Browse active DSA commission offers
     * For Lead Owners to find and compare DSA rates
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Paginated offers
     */
    browseOffers: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            const filterKeys = [
                'bank_name', 'loan_type', 'segment', 'city', 'state',
                'commission_min', 'commission_max', 'loan_amount', 'cibil_score',
                'search', 'sort_by', 'sort_order', 'per_page', 'page'
            ];

            filterKeys.forEach(key => {
                if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query ? `/leadflow/dsa-offers?${query}` : '/leadflow/dsa-offers';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.browseOffers:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get my posted offers (DSA dashboard)
     * @param {Object} filters - Filter parameters (status, search, loan_type, date_from, date_to, per_page, page)
     * @returns {Promise<Object>} My offers with stats and pagination
     */
    getMyOffers: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            ['status', 'search', 'loan_type', 'date_from', 'date_to', 'per_page', 'page'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query ? `/leadflow/dsa-offers/my?${query}` : '/leadflow/dsa-offers/my';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getMyOffers:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get offer details
     * @param {string} id - Offer UUID
     * @returns {Promise<Object>} Offer details
     */
    getOfferDetails: async (id) => {
        try {
            const response = await axiosInstance.get(`/leadflow/dsa-offers/${id}`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getOfferDetails:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Create a new DSA commission offer
     * @param {Object} data - Offer data
     * @returns {Promise<Object>} Created offer
     */
    createOffer: async (data) => {
        try {
            const response = await axiosInstance.post('/leadflow/dsa-offers', data);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.createOffer:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Update an existing offer
     * @param {string} id - Offer UUID
     * @param {Object} data - Fields to update
     * @returns {Promise<Object>} Updated offer
     */
    updateOffer: async (id, data) => {
        try {
            const response = await axiosInstance.put(`/leadflow/dsa-offers/${id}`, data);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.updateOffer:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Delete an offer
     * @param {string} id - Offer UUID
     * @returns {Promise<Object>} Deletion result
     */
    deleteOffer: async (id) => {
        try {
            const response = await axiosInstance.delete(`/leadflow/dsa-offers/${id}`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.deleteOffer:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Pause an offer (hide from browse)
     * @param {string} id - Offer UUID
     * @returns {Promise<Object>} Result
     */
    pauseOffer: async (id) => {
        try {
            const response = await axiosInstance.post(`/leadflow/dsa-offers/${id}/pause`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.pauseOffer:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Resume a paused offer
     * @param {string} id - Offer UUID
     * @returns {Promise<Object>} Result
     */
    resumeOffer: async (id) => {
        try {
            const response = await axiosInstance.post(`/leadflow/dsa-offers/${id}/resume`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.resumeOffer:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get offer analytics
     * @param {string} id - Offer UUID
     * @returns {Promise<Object>} Analytics data
     */
    getOfferAnalytics: async (id) => {
        try {
            const response = await axiosInstance.get(`/leadflow/dsa-offers/${id}/analytics`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getOfferAnalytics:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get bank suggestions for autocomplete
     * @param {string} search - Search term
     * @returns {Promise<Object>} Bank suggestions
     */
    getBankSuggestions: async (search = '') => {
        try {
            const response = await axiosInstance.get(`/leadflow/dsa-offers/banks?search=${encodeURIComponent(search)}`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getBankSuggestions:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get loan type options
     * @returns {Promise<Object>} Loan types
     */
    getLoanTypes: async () => {
        try {
            const response = await axiosInstance.get('/leadflow/dsa-offers/loan-types');
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getLoanTypes:', error);
            throw error.response?.data || error;
        }
    },

    // ================================
    // DSA INVITE MANAGEMENT
    // ================================

    /**
     * Send an invite to a DSA for a marketplace lead
     * Lead Owner action
     * @param {Object} data - Invite data
     * @param {string} data.marketplace_lead_id - Marketplace lead UUID
     * @param {string} data.dsa_offer_id - DSA offer UUID
     * @param {string} data.invite_message - Optional message
     * @param {number} data.expires_in_days - Days until expiry (1-30)
     * @returns {Promise<Object>} Created invite
     */
    sendInvite: async (data) => {
        try {
            const response = await axiosInstance.post('/leadflow/dsa-invites', data);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.sendInvite:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get invites sent by the Lead Owner
     * @param {Object} filters - Filter parameters
     * @param {string} [filters.status] - Filter by status (pending, viewed, interested, declined, awaiting)
     * @param {string} [filters.search] - Search in borrower name, DSA name
     * @param {string} [filters.loan_type] - Filter by loan/product type
     * @param {string} [filters.date_from] - Filter invites from this date (YYYY-MM-DD)
     * @param {string} [filters.date_to] - Filter invites until this date (YYYY-MM-DD)
     * @param {boolean} [filters.include_interest] - Whether to include linked interest data (for accepted invites)
     * @param {string} [filters.marketplace_lead_id] - Filter by specific lead
     * @param {number} [filters.per_page] - Number of results per page
     * @param {number} [filters.page] - Page number
     * @returns {Promise<Object>} Sent invites
     */
    getSentInvites: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            // Pagination and status filters
            ['status', 'marketplace_lead_id', 'per_page', 'page'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            // Additional filters: search, loan_type, date_from, date_to
            ['search', 'loan_type', 'date_from', 'date_to'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            // Handle include_interest boolean parameter
            if (typeof filters.include_interest === 'boolean') {
                params.append('include_interest', filters.include_interest ? '1' : '0');
            }

            const query = params.toString();
            const url = query ? `/leadflow/dsa-invites/sent?${query}` : '/leadflow/dsa-invites/sent';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getSentInvites:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get invites received by the DSA
     * @param {Object} filters - Filter parameters
     * @param {string} [filters.status] - Filter by status (pending, viewed, interested, declined)
     * @param {boolean} [filters.include_interest] - Whether to include linked interest data (for accepted invites)
     * @param {number} [filters.per_page] - Number of results per page
     * @param {number} [filters.page] - Page number
     * @returns {Promise<Object>} Received invites
     */
    getReceivedInvites: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            // Pagination and status filters
            ['status', 'per_page', 'page'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            // Additional filters: search, loan_type, date_from, date_to
            ['search', 'loan_type', 'date_from', 'date_to'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            // Handle include_interest boolean parameter
            if (typeof filters.include_interest === 'boolean') {
                params.append('include_interest', filters.include_interest ? '1' : '0');
            }

            const query = params.toString();
            const url = query ? `/leadflow/dsa-invites/received?${query}` : '/leadflow/dsa-invites/received';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getReceivedInvites:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get invite details
     * @param {string} id - Invite UUID
     * @returns {Promise<Object>} Invite details
     */
    getInviteDetails: async (id) => {
        try {
            const response = await axiosInstance.get(`/leadflow/dsa-invites/${id}`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getInviteDetails:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Mark invite as viewed (DSA action)
     * @param {string} id - Invite UUID
     * @returns {Promise<Object>} Result
     */
    markInviteViewed: async (id) => {
        try {
            const response = await axiosInstance.post(`/leadflow/dsa-invites/${id}/mark-viewed`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.markInviteViewed:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Accept an invite (DSA action)
     * Creates a marketplace_interest record
     * @param {string} id - Invite UUID
     * @param {Object} data - Optional message
     * @returns {Promise<Object>} Result with interest details
     */
    acceptInvite: async (id, data = {}) => {
        try {
            const response = await axiosInstance.post(`/leadflow/dsa-invites/${id}/accept`, {
                message: data.message
            });
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.acceptInvite:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Decline an invite (DSA action)
     * @param {string} id - Invite UUID
     * @param {Object} data - Optional reason
     * @returns {Promise<Object>} Result
     */
    declineInvite: async (id, data = {}) => {
        try {
            const response = await axiosInstance.post(`/leadflow/dsa-invites/${id}/decline`, {
                reason: data.reason
            });
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.declineInvite:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Cancel an invite (Lead Owner action)
     * @param {string} id - Invite UUID
     * @returns {Promise<Object>} Result
     */
    cancelInvite: async (id) => {
        try {
            const response = await axiosInstance.delete(`/leadflow/dsa-invites/${id}`);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.cancelInvite:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get invites for a specific marketplace lead
     * Lead Owner can see all invites sent for their lead
     * @param {string} leadId - Marketplace Lead UUID
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Invites for lead
     */
    getInvitesForLead: async (leadId, filters = {}) => {
        try {
            const params = new URLSearchParams();

            ['status', 'per_page'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query
                ? `/leadflow/dsa-invites/lead/${leadId}?${query}`
                : `/leadflow/dsa-invites/lead/${leadId}`;

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getInvitesForLead:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get invite summary counts (for badges/notifications)
     * @returns {Promise<Object>} Summary counts
     */
    getInviteSummary: async () => {
        try {
            const response = await axiosInstance.get('/leadflow/dsa-invites/summary');
            return response.data;
        } catch (error) {
            console.error('dsaOfferService.getInviteSummary:', error);
            throw error.response?.data || error;
        }
    }
};

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Get DSA offer status configuration
 * @param {string} status - Status string
 * @returns {Object} Status config
 */
export const getDsaOfferStatusConfig = (status) => {
    return DSA_OFFER_STATUS[status?.toLowerCase()] || {
        label: status || 'Unknown',
        severity: 'secondary',
        icon: 'pi-question'
    };
};

/**
 * Get DSA invite status configuration
 * @param {string} status - Status string
 * @returns {Object} Status config
 */
export const getDsaInviteStatusConfig = (status) => {
    return DSA_INVITE_STATUS[status?.toLowerCase()] || {
        label: status || 'Unknown',
        severity: 'secondary',
        icon: 'pi-question',
        description: ''
    };
};

/**
 * Get loan type configuration
 * @param {string} type - Loan type value
 * @returns {Object} Loan type config
 */
export const getDsaLoanTypeConfig = (type) => {
    return DSA_LOAN_TYPES.find(t => t.value === type) || {
        value: type,
        label: type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
        icon: 'pi-file'
    };
};

/**
 * Format commission percentage with proper decimals
 * @param {number} percentage - Commission percentage
 * @returns {string} Formatted percentage
 */
export const formatCommission = (percentage) => {
    if (percentage === null || percentage === undefined) return '-';
    return `${parseFloat(percentage).toFixed(3)}%`;
};

/**
 * Format loan amount range
 * @param {number} min - Minimum amount
 * @param {number} max - Maximum amount
 * @returns {string} Formatted range
 */
export const formatLoanRange = (min, max) => {
    const formatAmount = (amount) => {
        if (!amount) return '0';
        if (amount >= 10000000) {
            return `${(amount / 10000000).toFixed(2)} Cr`;
        } else if (amount >= 100000) {
            return `${(amount / 100000).toFixed(2)} L`;
        } else if (amount >= 1000) {
            return `${(amount / 1000).toFixed(0)}K`;
        }
        return amount.toString();
    };

    return `${formatAmount(min)} - ${formatAmount(max)}`;
};

/**
 * Calculate deadline urgency
 * @param {string} deadline - ISO date string
 * @returns {Object} Urgency info
 */
export const getDeadlineUrgency = (deadline) => {
    if (!deadline) return { level: 'none', label: 'No deadline', daysLeft: null };

    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diffMs = deadlineDate - now;
    const daysLeft = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (daysLeft < 0) {
        return { level: 'expired', label: 'Expired', daysLeft: 0, severity: 'danger' };
    } else if (daysLeft <= 3) {
        return { level: 'critical', label: `${daysLeft}d left`, daysLeft, severity: 'danger' };
    } else if (daysLeft <= 7) {
        return { level: 'urgent', label: `${daysLeft}d left`, daysLeft, severity: 'warning' };
    } else if (daysLeft <= 14) {
        return { level: 'normal', label: `${daysLeft}d left`, daysLeft, severity: 'info' };
    }
    return { level: 'relaxed', label: `${daysLeft}d left`, daysLeft, severity: 'success' };
};

/**
 * Format relative time
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/**
 * Filter banks by search term (client-side for quick autocomplete)
 * @param {string} search - Search term
 * @param {number} limit - Max results
 * @returns {Array} Filtered bank names
 */
export const filterBankSuggestions = (search, limit = 10) => {
    if (!search || search.length < 2) return POPULAR_BANKS.slice(0, limit);

    const searchLower = search.toLowerCase();
    return POPULAR_BANKS
        .filter(bank => bank.toLowerCase().includes(searchLower))
        .slice(0, limit);
};

export default dsaOfferService;
